import { useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import { translateText } from "../../utils/translation";
import { updateProfile } from "../../slices/auth";
import TextInput from "../forms/TextInput";

export default function Profile() {
  const dispatch = useAppDispatch();

  // Declare component level state
  const [profileForm, setProfileForm] = useState<{
    current_password: string;
    new_password: string;
    confirm_new_password: string;
  }>({
    current_password: "",
    new_password: "",
    confirm_new_password: "",
  });

  // Extract data from Redux store
  const { user, loading } = useAppSelector((state) => state.auth);
  const language = user === null ? "EN" : user.language;

  // Functions
  async function onProfileFormSubmit(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_new_password: confirmNewPassword,
    } = profileForm;

    // Validate text fields
    if (currentPassword === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_current_password", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    if (newPassword === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_new_password", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    if (confirmNewPassword === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_confirm_new_password", language),
          "danger",
          5000,
        ),
      );
      return;
    }

    // Validate passwords
    if (
      currentPassword === confirmNewPassword ||
      currentPassword === newPassword
    ) {
      dispatch(
        addAlert(generateAlertText("same_passwords", language), "danger", 5000),
      );
      return;
    }
    if (newPassword !== confirmNewPassword) {
      dispatch(
        addAlert(
          generateAlertText("different_passwords", language),
          "danger",
          5000,
        ),
      );
      return;
    }

    const res: any = await dispatch(
      updateProfile({
        language,
        username: user.username,
        currentPassword,
        password: newPassword,
        confirmPassword: confirmNewPassword,
        timezone: user.timezone[0],
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_profile", language),
          "success",
          3000,
        ),
      );
      setProfileForm({
        current_password: "",
        new_password: "",
        confirm_new_password: "",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
    }
  }

  return (
    <Template>
      {/* Header */}
      <Header text={translateText("profile_title", language)} />

      {/* Loading spinner */}
      {loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <div className="rounded-lg p-8 overflow-x-auto drop-shadow scrollbar-invisible mx-1 mb-1 bg-white">
              <div className="grid grid-cols-1 gap-y-8">
                {/* Current password */}
                <TextInput
                  id="current_password"
                  type="password"
                  label={translateText("current_password", language)}
                  className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                  required={true}
                  showLabel={true}
                  state={profileForm}
                  stateName="current_password"
                  onChange={setProfileForm}
                />

                {/* New password */}
                <TextInput
                  id="new_password"
                  type="password"
                  label={translateText("new_password", language)}
                  className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                  required={true}
                  showLabel={true}
                  state={profileForm}
                  stateName="new_password"
                  onChange={setProfileForm}
                />

                {/* Confirm new password */}
                <TextInput
                  id="confirm_new_password"
                  type="password"
                  label={translateText("confirm_new_password", language)}
                  className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                  required={true}
                  showLabel={true}
                  state={profileForm}
                  stateName="confirm_new_password"
                  onChange={setProfileForm}
                />

                {/* Submit button */}
                <Button
                  type="button"
                  className={`${getButtonStyle("solid", "success")}`}
                  onClick={onProfileFormSubmit}
                >
                  <CheckBadgeIcon
                    className={`h-5 w-5 text-white mr-1`}
                    aria-hidden="true"
                  />
                  {translateText("save_profile", language)}
                </Button>
              </div>
            </div>
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
